import * as React from "react";
import { useEffect, useState } from "react";
import OutsideClickHandler from "@graffer-inc/react-outside-click-handler";
import { useModal } from "../../../../utils/custom-hooks";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { useHistory, useLocation, useParams } from "react-router-dom";
import { formatDatetime } from "../../../../utils/date-utils";
import { ProcedureStatusTag } from "../../../molecules/procedure-status-tag";
import {
  OrganizationProceduresService,
  OrganizationProceduresServiceProps,
} from "../../../../application/service/service-provider/procedures-service";
import { ServiceProviderTabs } from "../../../molecules/service-provider-tab";
import { BaseModal } from "../../../atoms/base-modal";
import { ProcedureCreateForm } from "../../../organisms/smart-apply/procedure-edit/create-form";
import {
  GrafferManagedTag,
  reportUnexpectedError,
  connectPageService,
} from "@graffer-inc/smart-apply";
import {
  ApplicantSchemaKeysMigrator,
  createInitialCloudGovSchema,
} from "@graffer-inc/cloud-gov-core";
import { ServiceProviderR } from "../routes";
import { parseQueryOfLocation } from "../../../../utils/url-utils";
import { Dropdown } from "../../../molecules/dropdown";
import {
  PrimaryButton,
  SecondaryButton,
} from "@graffer-inc/console-design-kit";
import { StatusFilterDropDown } from "../../../organisms/smart-apply/procedure-list/status-filter-dropdown";
import { LoadingModal } from "../../../organisms/smart-apply/modals/loading-modal";
import { Pagination } from "@graffer-inc/console-design-kit";
import { ProcedureImportFromFileModal } from "../../../organisms/service-provider/modal/pdf-uploader-modal/procedure-import-from-file-modal";
import { toast } from "react-toastify";
import { ProceduresSearchWindow } from "../../../organisms/smart-apply/procedure-list/procedures-search-window";
import { UpdateProcedureGroupModal } from "../../../organisms/smart-apply/modals/update-procedure-group-modal";
import { MultiGroupSelect } from "../../../organisms/smart-apply/group-select/multi-group-select";
import { useFeatureFlag_EnableApplyAssignment } from "../../../../feature-flags";
import apiClient from "../../../../application/apis/api-client";
import FileSaver from "file-saver";

export const OrganizationProceduresList: React.FunctionComponent<
  OrganizationProceduresServiceProps
> = ({
  isBulkDownloading,
  procedures,
  paging,
  setPage,
  movePage,
  queryName,
  setName,
  fetchProceduresByOrgId,
  createProcedure,
  searchProcedures,
  importProcedureFromFiles,
  checkProcedureImportFile,
  selectedStatus,
  setSelectedStatus,
  onBulkExport,
  selectedProcedureIds,
  selectAll,
  selectToggle,
  isAllSelected,
  onDownloadCsvForUpdateGroup,
  bulkUpdateGroupPreview,
  bulkUpdateGroup,
  selectedGroupIds,
  setSelectedGroupIds,
}) => {
  const { page, name, groupIds } = parseQueryOfLocation(useLocation()) as {
    page: string | undefined;
    name: string | undefined;
    groupIds: string | undefined;
  };
  const { id: orgId } = useParams<{ id: string }>();
  const history = useHistory();
  const [isImportLoading, setIsImportLoading] = useState<boolean>(false);
  const procedureModal = useModal();
  const menuToggle = useModal();
  const exportProcedureModal = useModal();
  const updateProcedureGroupModal = useModal();

  // to be used only for confirmation of assignee settings
  const [orgSlug, setOrgSlug] = useState<string>("");
  const [orgName, setOrgName] = useState<string>("");
  const resolveOrg = async (orgId: string) => {
    const { data: organization } = await apiClient.getOrganization(orgId);
    setOrgSlug(organization.slug);
    setOrgName(organization.name);
  };

  const createTFDAssigneeSettingsExcel = async (orgId: string) => {
    const { data } = await apiClient.downloadAssignSettingsXlsx(orgId);
    const now = new Date();
    const padZero = (num: number) => num.toString().padStart(2, "0");
    const dateStr = `${now.getFullYear()}${padZero(now.getMonth() + 1)}${padZero(now.getDate())}${padZero(now.getHours())}${padZero(now.getMinutes())}`;
    FileSaver.saveAs(
      new Blob([data]),
      `${orgName}-振り分け設定-${dateStr}.xlsx`,
    );
  };

  useEffect(() => {
    setPage(parseInt(page!, 10) || 1);
    setName(name || "");
    setSelectedGroupIds(groupIds ? groupIds.split(",").filter(Boolean) : []);
    fetchProceduresByOrgId(orgId);
    resolveOrg(orgId);

    // eslint-disable-next-line
  }, [
    page,
    name,
    groupIds,
    setPage,
    setName,
    setSelectedGroupIds,
    fetchProceduresByOrgId,
    selectedStatus,
  ]);

  return (
    <ServiceProviderTemplate
      breadcrumbs={[{ to: ServiceProviderR.Home.link({}), title: "Home" }]}
    >
      <div className="has-text-left">
        <div className="columns">
          <div className="column">
            <ServiceProviderTabs orgId={orgId} />
            <div className="columns is-multiline">
              <div className="column is-narrow">
                <ProceduresSearchWindow
                  searchName={queryName}
                  setSearchName={setName}
                  searchProcedures={searchProcedures}
                />
              </div>
              <div className="column">
                <MultiGroupSelect
                  selectedIds={selectedGroupIds}
                  onSelect={(ids) => {
                    setSelectedGroupIds(ids || []);
                    searchProcedures();
                  }}
                  tenantId={orgId}
                  width="80%"
                />
              </div>
              {useFeatureFlag_EnableApplyAssignment(orgSlug) && (
                <div className="column">
                  <SecondaryButton
                    text={"振り分け設定出力"}
                    onClick={() => createTFDAssigneeSettingsExcel(orgId)}
                  />
                </div>
              )}

              <div className="column has-text-right is-right is-narrow">
                <div className="has-text-right buttons is-right">
                  <PrimaryButton
                    text={"新規追加"}
                    onClick={procedureModal.open}
                  />
                  <OutsideClickHandler onOutsideClick={menuToggle.close}>
                    <Dropdown
                      onClick={menuToggle.open}
                      isActive={menuToggle.isActive}
                      isRight={true}
                      menus={[
                        {
                          title: "手続きをインポートする",
                          onClick: exportProcedureModal.open,
                        },
                        {
                          title: "csvで一括エクスポート",
                          onClick: () =>
                            history.push(
                              ServiceProviderR.SmartApplyProceduresBulkExportByCSV.link(
                                {
                                  id: orgId,
                                },
                              ),
                            ),
                        },
                        {
                          title: "csvで一括インポート",
                          onClick: () =>
                            history.push(
                              ServiceProviderR.SmartApplyProceduresBulkImportByCSV.link(
                                {
                                  id: orgId,
                                },
                              ),
                            ),
                        },
                        {
                          title: "管理グループ一括更新",
                          onClick: updateProcedureGroupModal.open,
                        },
                      ]}
                    />
                  </OutsideClickHandler>
                </div>
              </div>
            </div>
            <table className="table is-fullwidth">
              <thead>
                <tr>
                  <th className="pr-0" style={{ width: "50px" }}>
                    <input
                      checked={isAllSelected()}
                      type="checkbox"
                      onClick={(e) => {
                        e.stopPropagation();
                        selectAll();
                      }}
                    />
                  </th>
                  <th>手続きID</th>
                  <th>手続き名称</th>
                  <th>
                    <StatusFilterDropDown
                      selectedStatus={selectedStatus}
                      onSelect={setSelectedStatus}
                    >
                      公開ステータス
                    </StatusFilterDropDown>
                  </th>
                  <th>受付開始日</th>
                  <th>受付終了日</th>
                </tr>
              </thead>
              <tbody>
                {selectedProcedureIds.length > 0 && (
                  <tr>
                    <td colSpan={5} style={{ borderBottom: "none" }}>
                      <div className="buttons">
                        <SecondaryButton
                          isSmall
                          text="一括エクスポート"
                          onClick={onBulkExport}
                        />
                      </div>
                    </td>
                    <td
                      className="has-text-right"
                      style={{ borderBottom: "none" }}
                    >
                      <span className="has-text-weight-bold">
                        {selectedProcedureIds.length}
                      </span>
                      {" 件選択中"}
                    </td>
                  </tr>
                )}
                {procedures.map((procedure) => (
                  <tr
                    key={procedure.id}
                    className="c-tr-selectable c-tr-has-tag"
                    onClick={() =>
                      history.push(
                        ServiceProviderR.ProcedureDetail.link({
                          orgId: orgId,
                          procedureId: procedure.id,
                        }),
                      )
                    }
                  >
                    <td
                      onClick={(e) => {
                        e.stopPropagation();
                        selectToggle(procedure.id);
                      }}
                    >
                      <input
                        checked={selectedProcedureIds.includes(procedure.id)}
                        type="checkbox"
                        onClick={(e) => {
                          e.stopPropagation();
                          selectToggle(procedure.id);
                        }}
                      />
                    </td>
                    <td>{procedure.id}</td>
                    <td>
                      {procedure.name}
                      {procedure.isGrafferManaged && (
                        <>
                          &nbsp; &nbsp;
                          <GrafferManagedTag />
                        </>
                      )}
                    </td>
                    <td>
                      <ProcedureStatusTag status={procedure.publishStatus} />
                    </td>
                    <td>
                      {procedure.publishSetting.startAcceptAt
                        ? formatDatetime(procedure.publishSetting.startAcceptAt)
                        : "設定なし"}
                    </td>
                    <td>
                      {procedure.publishSetting.endAcceptAt
                        ? formatDatetime(procedure.publishSetting.endAcceptAt)
                        : "設定なし"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: "16px" }}>
              <Pagination
                onPageClick={movePage}
                currentPage={paging.current}
                lastPage={paging.total}
              />
            </div>
          </div>
        </div>
      </div>
      <LoadingModal
        isActive={isBulkDownloading}
        title="一括エクスポート"
        body="ダウンロードしています。"
      />
      <BaseModal
        isActive={procedureModal.isActive}
        title="新規手続き作成"
        onClose={procedureModal.close}
        hasOverflow={true}
      >
        <ProcedureCreateForm
          initialValues={{
            acceptAnyFile: false,
            requireAuthLevel: {
              individual: "NONE",
              corporation: "NONE",
            },
            name: "",
            applicantType: [],
          }}
          orgId={orgId}
          onSubmit={async (values) => {
            try {
              const formSchema = createInitialCloudGovSchema(
                values.applicantType,
              );
              const applicantSchemaKeys =
                ApplicantSchemaKeysMigrator.up(formSchema);
              if (applicantSchemaKeys instanceof Error) {
                throw applicantSchemaKeys;
              }
              const proc = await createProcedure(orgId, {
                ...values,
                template: {
                  formSchema,
                },
                applicantSchemaKeys,
              });
              if (proc) {
                history.push(
                  ServiceProviderR.ProcedureDetail.link({
                    orgId,
                    procedureId: proc.id,
                  }),
                );
              }
            } catch (e) {
              reportUnexpectedError(e);
            } finally {
              procedureModal.close();
            }
          }}
          onClose={procedureModal.close}
        />
      </BaseModal>
      <ProcedureImportFromFileModal
        orgId={orgId}
        isActive={exportProcedureModal.isActive}
        isLoading={isImportLoading}
        close={exportProcedureModal.close}
        checkImportFile={checkProcedureImportFile}
        onSave={async (jsonFiles, groupId, customPathClearTargets) => {
          setIsImportLoading(true);
          const { importedProcIds, errorMessages } =
            await importProcedureFromFiles(
              jsonFiles,
              orgId,
              groupId,
              customPathClearTargets,
            );
          setIsImportLoading(false);
          if (importedProcIds.length === 0) {
            return { importedProcIds, errorMessages };
          }
          if (jsonFiles.length === 1) {
            toast.success(
              `${importedProcIds.length}件の手続きをインポートしました。`,
            );
            history.push(
              ServiceProviderR.ProcedureDetail.link({
                orgId: orgId,
                procedureId: importedProcIds[0],
              }),
            );
          } else {
            fetchProceduresByOrgId(orgId);
          }
          return { importedProcIds, errorMessages };
        }}
      />
      {updateProcedureGroupModal.isActive && (
        <UpdateProcedureGroupModal
          isActive={updateProcedureGroupModal.isActive}
          onClose={updateProcedureGroupModal.close}
          orgId={orgId}
          downloadCsvForUpdateGroup={onDownloadCsvForUpdateGroup}
          bulkUpdateGroupPreview={bulkUpdateGroupPreview}
          bulkUpdateGroup={bulkUpdateGroup}
        />
      )}
    </ServiceProviderTemplate>
  );
};

export const OrganizationProceduresListContainer = connectPageService(
  OrganizationProceduresList,
  () => new OrganizationProceduresService(),
);
